// Fees: a student's payments and receipts; for admins, all payments (website and others), linking
// website payments to students, adding payments made another way, and the receipt details.
import { useEffect, useLayoutEffect, useState } from 'preact/hooks';
import { Link } from '../router.js';
import { rpc } from '../api.js';
import { useApp } from '../state.js';
import { Icon } from '../ui/icons.js';
import { Shell } from '../ui/shell.jsx';
import { Button, Chip, Empty, ErrorBox, Field, Loading, Notice, Section, Sheet, useRpc, useToast } from '../ui/components.jsx';
import { dateLong, dateShort, methodLabel, rupees, rupeesInWords, todayIso } from '../format.js';

const same = (name) => String(name || '').toLowerCase().replace(/\s+/g, ' ').trim();

export function PaymentRow({ x, admin, onLink }) {
  const refunded = x.refunded_paise > 0;
  return (
    <li class={`pay-row${x.cancelled ? ' is-cancelled' : ''}`}>
      <div class="pay-amount">
        <strong>{rupees(x.amount_paise)}</strong>
        <small>{dateShort(x.paid_at)}</small>
      </div>
      <div class="pay-text">
        <strong>{x.description}</strong>
        <small>
          {methodLabel(x.method)}{x.source === 'razorpay' ? ' · online' : ''} · {x.receipt_no}
          {admin ? ` · ${x.person ? (x.person.full_name || x.person.email) : [x.payer.name, x.payer.email, x.payer.phone].filter(Boolean).join(', ')}` : ''}
        </small>
        <span class="pay-chips">
          {x.cancelled ? <Chip tone="bad">Cancelled</Chip> : null}
          {refunded ? <Chip tone="gold">Refunded {rupees(x.refunded_paise)}</Chip> : null}
          {admin && !x.person && !x.cancelled && !x.not_student ? <Chip tone="gold">Not linked</Chip> : null}
          {admin && !x.person && x.not_student ? <Chip>Not a student</Chip> : null}
          {admin && x.person && (x.linked_by === 'phone' || x.linked_by === 'email')
            ? <Chip title="Linked by itself: the payer’s details match this student’s">Matched by {x.linked_by}</Chip> : null}
        </span>
      </div>
      <div class="pay-actions">
        {admin && !x.person && !x.cancelled && !x.not_student
          ? <Button variant="gold" size="sm" icon="link" onClick={() => onLink(x)}>Link</Button>
          : admin ? <Button variant="ghost" size="sm" icon="edit" onClick={() => onLink(x)} aria-label={`Change payment ${x.receipt_no}`}>Change</Button> : null}
        <Link class="btn btn-line btn-sm" href={`/receipts/${x.id}`} aria-label={`Receipt ${x.receipt_no}`}><Icon name="receipt" /><span>Receipt</span></Link>
      </div>
    </li>
  );
}

/* ---------------------------------------------------------------- Student */
export function Fees() {
  const { data, error, loading, reload } = useRpc('my_payments');
  return (
    <Shell title="Fees and receipts" backTo="/profile" backLabel="Profile">
      <p class="muted">Payments you have made to the academy, with a receipt for each. Paid on the website? It appears here once the academy has matched it to your account.</p>
      {loading && !data ? <Loading rows={3} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {data && !data.payments.length ? <Empty icon="receipt" title="No payments yet">Your payments and receipts will show up here.</Empty> : null}
      {data && data.payments.length ? <ul class="list pay-list">{data.payments.map((x) => <PaymentRow key={x.id} x={x} />)}</ul> : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- Receipt (print or save as PDF) */
export function Receipt({ id }) {
  const { me } = useApp();
  const { data, error, loading, reload } = useRpc('get_receipt', { p_payment_id: id }, [id]);
  const x = data;
  const a = (x && x.academy) || {};
  // Website payments: the name typed at checkout (a parent, say), and the student it is for.
  const student = x && x.person ? (x.person.full_name || x.person.email) : '';
  const typed = x && x.source === 'razorpay' ? (x.payer.name || '').trim() : '';
  const from = typed || student || (x && x.payer.email) || 'Student';
  const back = me.role === 'admin' ? ['/admin/payments', 'Payments']
    : me.role === 'parent' ? (x && x.person ? [`/children/${x.person.id}`, x.person.full_name || 'Student'] : ['/', 'Home'])
      : ['/fees', 'Fees'];
  return (
    <Shell backTo={back[0]} backLabel={back[1]}>
      {loading && !x ? <Loading rows={3} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {x ? (
        <>
          <article class="receipt" aria-label={`Receipt ${x.receipt_no}`}>
            <header class="receipt-head">
              <div class="receipt-from">
                <span class="mark" aria-hidden="true" />
                <div>
                  <p class="receipt-org">{a.name || 'Vidyavedh Academy LLP'}</p>
                  {a.address ? <p>{a.address}</p> : null}
                  {a.phone ? <p>{a.phone}</p> : null}
                  {a.email ? <p>{a.email}</p> : null}
                  {a.gstin ? <p>GSTIN {a.gstin}</p> : null}
                </div>
              </div>
              <div class="receipt-title">
                <h1>Payment receipt</h1>
                <p>No. <strong>{x.receipt_no}</strong></p>
                <p>{dateLong(x.paid_at)}</p>
              </div>
            </header>
            {x.cancelled ? <p class="receipt-void">Cancelled</p> : null}
            <dl class="receipt-lines">
              <div><dt>Received from</dt><dd>{from}{student && same(student) !== same(from) ? <small>For student {student}</small> : x.person && x.person.full_name ? <small>{x.person.email}</small> : null}</dd></div>
              <div><dt>For</dt><dd>{x.description}</dd></div>
              <div><dt>Amount</dt><dd><strong class="receipt-amount">{rupees(x.amount_paise)}</strong><small>{rupeesInWords(x.amount_paise)}</small></dd></div>
              <div><dt>Paid by</dt><dd>{methodLabel(x.method) || 'Online'}{x.razorpay_payment_id ? <small>Razorpay payment {x.razorpay_payment_id}</small> : null}{x.reference ? <small>Reference {x.reference}</small> : null}</dd></div>
              {x.refunded_paise > 0 ? <div><dt>Refunded</dt><dd>{rupees(x.refunded_paise)}</dd></div> : null}
            </dl>
            <p class="receipt-foot">This receipt was made by the academy’s learning app and needs no signature.</p>
          </article>
          <div class="row no-print">
            <Button variant="navy" icon="print" onClick={() => window.print()}>Print or save as PDF</Button>
          </div>
        </>
      ) : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- Admin */
export function AdminPayments() {
  const toast = useToast();
  const [filter, setFilter] = useState(null);
  const { data, error, loading, reload } = useRpc('admin_payments', { p_filter: filter }, [filter]);
  const [linking, setLinking] = useState(null);
  const [adding, setAdding] = useState(false);
  return (
    <Shell title="Payments" backTo="/admin" backLabel="Admin"
      actions={<Button variant="gold" size="sm" icon="plus" onClick={() => setAdding(true)}>Add a payment</Button>}>
      {loading && !data ? <Loading rows={4} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {data ? (
        <>
          <div class="stats stats-3">
            <div class="stat"><strong>{rupees(data.month_paise)}</strong><span>This month</span></div>
            <div class="stat"><strong>{rupees(data.fy_paise)}</strong><span>Financial year {data.fy}</span></div>
            <div class="stat"><strong>{data.unlinked}</strong><span>Website payments to link</span></div>
          </div>
          <div class="filters" role="group" aria-label="Show">
            <button type="button" class={`quick-btn${filter === null ? ' is-on' : ''}`} aria-pressed={filter === null ? 'true' : 'false'} onClick={() => setFilter(null)}>All</button>
            <button type="button" class={`quick-btn${filter === 'unlinked' ? ' is-on' : ''}`} aria-pressed={filter === 'unlinked' ? 'true' : 'false'} onClick={() => setFilter('unlinked')}>Not linked ({data.unlinked})</button>
          </div>
          {!data.payments.length ? (
            <Empty icon="receipt" title={filter ? 'Everything is linked' : 'No payments yet'}>
              {filter ? 'Every website payment is matched to a student.' : 'Website payments arrive here by themselves once Razorpay is connected. Add bank transfers and cash with Add a payment.'}
            </Empty>
          ) : (
            <ul class="list pay-list">{data.payments.map((x) => <PaymentRow key={x.id} x={x} admin onLink={setLinking} />)}</ul>
          )}
          <p class="fine"><Icon name="info" />Website payments link themselves when the payer’s email or phone is saved on a student (People &gt; pencil). Link the rest here once; the app remembers their details for next time.</p>
        </>
      ) : null}
      <PaymentSheet x={linking} open={!!linking} onClose={() => setLinking(null)} onSaved={(msg) => { reload(true); toast(msg); }} />
      <AddPayment open={adding} onClose={() => setAdding(false)} onSaved={(no) => { reload(true); toast(`Payment added: receipt ${no}`); }} />
    </Shell>
  );
}

// One payment: link it to a student (or to someone else), unlink it, mark a visitor's payment as
// not a student's, or cancel a payment entered by mistake.
function PaymentSheet({ x, open, onClose, onSaved }) {
  const [email, setEmail] = useState('');
  const [remember, setRemember] = useState(true);
  const [forget, setForget] = useState(true);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState(null);
  const [confirm, setConfirm] = useState(false);
  useLayoutEffect(() => { if (open) { setErr(null); setEmail(''); setConfirm(false); setRemember(true); setForget(true); } }, [open]);
  if (!x) return null;
  const run = async (what, fn, message) => {
    setBusy(what); setErr(null);
    try { const res = await fn(); onSaved(typeof message === 'function' ? message(res) : message); onClose(); } catch (ex) { setErr({ what, message: ex.message }); }
    setBusy('');
  };
  const web = x.source === 'razorpay';
  const payerBits = [x.payer.phone, x.payer.email].filter(Boolean);
  const canRemember = web && payerBits.length > 0;
  // The detail that made it link itself (an academy email can't be removed, so it isn't offered).
  const matched = x.person && x.linked_by === 'phone' ? x.payer.phone
    : x.person && x.linked_by === 'email' && x.payer.email && x.payer.email !== x.person.email ? x.payer.email : '';
  const linkedMsg = (res) => {
    const name = res.person.full_name || res.person.email;
    const saved = Object.values(res.saved || {});
    return saved.length ? `Linked to ${name}. Saved ${saved.join(' and ')} for next time.` : `Linked to ${name}`;
  };
  const link = (e) => {
    e.preventDefault();
    run('link', () => rpc('admin_link_payment', {
      p_payment_id: x.id, p_email: email, p_remember: canRemember && remember, p_forget_match: Boolean(matched) && forget,
    }), linkedMsg);
  };
  const payer = [x.payer.name, x.payer.email, x.payer.phone].filter(Boolean).join(', ');
  const who = x.person ? (x.person.full_name || x.person.email) : '';
  return (
    <Sheet title={x.person ? `${rupees(x.amount_paise)} from ${who}` : `Link ${rupees(x.amount_paise)} to a student`} open={open} onClose={onClose}>
      <div class="form">
        <Notice icon="info">
          Paid on {dateShort(x.paid_at)} for {x.description}{web ? ` by ${payer || 'someone who left no details'}` : ''}. Receipt {x.receipt_no}.
          {x.person ? ` Linked to ${who} (${x.person.email})${matched ? `, by itself: ${x.linked_by === 'phone' ? 'the phone' : 'the email'} ${matched} is saved on their details` : ''}.` : ''}
        </Notice>
        {!x.cancelled ? (
          <form class="form" onSubmit={link}>
            <Field label={x.person ? 'Link to another student instead: their academy email' : 'The student’s academy email'} error={err && err.what === 'link' ? err.message : null}>{(id) => (
              <input id={id} type="email" required autofocus={!x.person} value={email} onInput={(e) => setEmail(e.currentTarget.value)} placeholder="name@vidyavedhacademyllp.onmicrosoft.com" />
            )}</Field>
            {canRemember ? (
              <label class="check"><input type="checkbox" checked={remember} onChange={(e) => setRemember(e.currentTarget.checked)} />
                <span>Save {payerBits.join(' and ')} on the student’s details, so their next website payments link by themselves (only fills empty ones)</span></label>
            ) : null}
            <div class="form-actions">
              <Button type="submit" variant="navy" icon="link" busy={busy === 'link'}>{x.person ? 'Link to them' : 'Link'}</Button>
              <Button variant="ghost" onClick={onClose}>Close</Button>
            </div>
          </form>
        ) : null}

        {x.person ? (
          <div class="sheet-more">
            <h3>Wrong student?</h3>
            <p class="muted">Unlinking takes it off {who}’s fees. Any details this payment saved on them are removed too.</p>
            {matched ? (
              <label class="check"><input type="checkbox" checked={forget} onChange={(e) => setForget(e.currentTarget.checked)} />
                <span>Also remove {matched} from {who}’s details, so this payer’s next payment waits for you</span></label>
            ) : null}
            <Button variant="line" icon="close" busy={busy === 'unlink'}
              onClick={() => run('unlink', () => rpc('admin_unlink_payment', { p_payment_id: x.id, p_forget_match: Boolean(matched) && forget }), 'Unlinked')}>Unlink from {who}</Button>
          </div>
        ) : null}

        {!x.person && web && !x.not_student ? (
          <div class="sheet-more">
            <h3>Not one of your students?</h3>
            <p class="muted">For example, a visitor who booked a guidance session. It stays on the list but stops counting as one to link.</p>
            <Button variant="line" busy={busy === 'visitor'}
              onClick={() => run('visitor', () => rpc('admin_mark_not_student', { p_payment_id: x.id }), 'Marked as not a student')}>Not a student</Button>
          </div>
        ) : null}

        {!web && !x.cancelled ? (
          <div class="sheet-more">
            <h3>Entered by mistake?</h3>
            <p class="muted">Cancelling keeps the receipt number (marked cancelled) so the numbers stay in order, and takes it out of the totals.</p>
            {confirm ? (
              <div class="row">
                <Button variant="danger" icon="trash" busy={busy === 'cancel'}
                  onClick={() => run('cancel', () => rpc('admin_cancel_payment', { p_payment_id: x.id }), 'Payment cancelled')}>Yes, cancel this payment</Button>
                <Button variant="ghost" onClick={() => setConfirm(false)}>Keep it</Button>
              </div>
            ) : <Button variant="line" icon="trash" onClick={() => setConfirm(true)}>Cancel this payment</Button>}
          </div>
        ) : null}
        {err && err.what !== 'link' ? <p class="field-error" role="alert">{err.message}</p> : null}
        {web ? <p class="fine"><Icon name="info" />Refunds are made in Razorpay; they show here by themselves.</p> : null}
        {x.cancelled ? <div class="form-actions"><Button variant="ghost" onClick={onClose}>Close</Button></div> : null}
      </div>
    </Sheet>
  );
}

const MANUAL_METHODS = [['neft', 'Bank transfer (NEFT/IMPS)'], ['upi', 'UPI'], ['cash', 'Cash'], ['cheque', 'Cheque'], ['card', 'Card'], ['other', 'Other']];

function AddPayment({ open, onClose, onSaved }) {
  const blank = () => ({ email: '', amount: '', method: 'neft', date: todayIso(), description: '', reference: '' });
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useLayoutEffect(() => { if (open) { setErr(null); setF(blank()); } }, [open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.currentTarget.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const res = await rpc('admin_add_payment', {
        p_email: f.email, p_amount_rupees: Number(String(f.amount).replace(/[,\s₹]/g, '')), p_method: f.method,
        p_paid_on: f.date, p_description: f.description, p_reference: f.reference,
      });
      onSaved(res.receipt_no);
      onClose();
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };
  return (
    <Sheet title="Add a payment" open={open} onClose={onClose}>
      <form class="form" onSubmit={save}>
        <Field label="Student’s academy email">{(id) => <input id={id} type="email" required autofocus value={f.email} onInput={set('email')} placeholder="name@vidyavedhacademyllp.onmicrosoft.com" />}</Field>
        <div class="row-2">
          <Field label="Amount (₹)">{(id) => <input id={id} type="text" inputMode="decimal" required value={f.amount} onInput={set('amount')} placeholder="70000" />}</Field>
          <Field label="Paid on">{(id) => <input id={id} type="date" required max={todayIso()} value={f.date} onInput={set('date')} />}</Field>
        </div>
        <Field label="How">{(id) => (
          <select id={id} value={f.method} onChange={set('method')}>{MANUAL_METHODS.map(([v, l]) => <option value={v} key={v}>{l}</option>)}</select>
        )}</Field>
        <Field label="For">{(id) => <input id={id} type="text" required maxLength={200} value={f.description} onInput={set('description')} placeholder="Silver plan" />}</Field>
        <Field label="Reference (optional)" hint="UTR number, cheque number or a note.">{(id) => <input id={id} type="text" maxLength={120} value={f.reference} onInput={set('reference')} />}</Field>
        {err ? <p class="field-error" role="alert">{err}</p> : null}
        <div class="form-actions"><Button type="submit" variant="navy" icon="plus" busy={busy}>Add payment</Button><Button variant="ghost" onClick={onClose}>Cancel</Button></div>
      </form>
    </Sheet>
  );
}

// In Admin > Settings: what every receipt shows at the top.
export function ReceiptDetails() {
  const toast = useToast();
  const { data, error, reload } = useRpc('admin_receipt_details');
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useLayoutEffect(() => { if (data) setF({ name: data.name || '', address: data.address || '', phone: data.phone || '', email: data.email || '', gstin: data.gstin || '' }); }, [data]);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!f) return <Loading rows={1} />;
  const set = (k) => (e) => setF({ ...f, [k]: e.currentTarget.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await rpc('admin_save_receipt_details', { p_details: f }); toast('Receipt details saved'); reload(true); } catch (ex) { toast(ex.message, 'bad'); }
    setBusy(false);
  };
  return (
    <form class="card form" onSubmit={save}>
      <Field label="Name">{(id) => <input id={id} type="text" required value={f.name} onInput={set('name')} />}</Field>
      <Field label="Address">{(id) => <textarea id={id} rows={2} value={f.address} onInput={set('address')} />}</Field>
      <div class="row-2">
        <Field label="Phone">{(id) => <input id={id} type="tel" value={f.phone} onInput={set('phone')} />}</Field>
        <Field label="Email">{(id) => <input id={id} type="email" value={f.email} onInput={set('email')} />}</Field>
      </div>
      <Field label="GSTIN (optional)" hint="Shown on receipts if you add it. These are payment receipts; ask your CA whether you also need GST tax invoices.">{(id) => (
        <input id={id} type="text" maxLength={15} value={f.gstin} onInput={set('gstin')} />
      )}</Field>
      <div class="form-actions"><Button type="submit" variant="navy" busy={busy}>Save receipt details</Button></div>
    </form>
  );
}

export function FeesLink() {
  return (
    <Section title="Fees and receipts" id="p-fees">
      <Link class="tile" href="/fees"><Icon name="receipt" /><span><strong>Your payments</strong><small>Receipts you can print or save</small></span><Icon name="next" /></Link>
    </Section>
  );
}
